import axios from 'axios'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'

import DurationWFFMDataExchange from '@/services/DurationWFFMDataExchange'
import { getValidRanges } from '@/utils/getValidRanges'
import QueryString from '@/utils/QueryString'

const $ = require('jquery')

const URL_DOMAIN = (`${window.location.protocol}//${window.location.host}`)
const URL_BASE_API = `${URL_DOMAIN}/api/sitecore`
const URL_BASE_API2 = `${URL_DOMAIN}/api`

export const URL_API = {
  'LoadMoreOffers': `${URL_BASE_API}/OffersList/LoadMoreOffers`,
  'LoadMoreSimilarOffers': `${URL_BASE_API}/OffersList/LoadMoreSimilarOffers`,
  'LoadOfferFood': `${URL_BASE_API}/OffersList/LoadOfferFood`,
  'LoadOfferAirports': `${URL_BASE_API}/OffersList/LoadOfferAirports`,
  'loadGalleryList': `${URL_BASE_API}/Hotel/GetHotelPhotosIn3SizesAsJson?w=1280&h=720&HotelGuid=`,
  'AllHotelOffers': `${URL_BASE_API}/OffersList/AllHotelOffers`,
  'HotelAllOffersAsJson': `${URL_BASE_API}/Hotel/HotelAllOffersAsJson`,
  'GetBookingWidget': `${URL_BASE_API}/Hotel/BookingWidgetAJAX`,
  'GetBookingWidgetOffer': `${URL_BASE_API}/Hotel/GetBookingWidgetOffer`,
  'DolphinClubPhotos': `${URL_BASE_API}/Hotel/DolphinClubPhotos`,
  'offerspopup': `${URL_BASE_API2}/OffersFilter?hotelcode=`,
  'GetAdditionalServicesJSON': `${URL_BASE_API}/payment/GetAdditionalServicesJSON`,
  'GetFinalPriceJSON': `${URL_BASE_API}/payment/GetFinalPriceJSON`,
  'pageSearch': `${URL_BASE_API}/PageSearch/GetHotelsFromSearchIndex`,
  'pageSearchWordsBlacklist': `${URL_BASE_API}/PageSearch/GetBlacklistWords`,
  'salespointsMap': `${URL_BASE_API}/salemap/GetSalespointsJsonData`,
  'pageAgreements': `${URL_BASE_API}/StaticPage/GetRegulations`,
}

export const URL_TEMPLATES = '/api/'

export const BREAKPOINT = {
  MOBILE: 320,
  TABLET: 767,
  DESKTOP: 1349,
}

export const BREAKPOINT_FLUID = {
  MOBILE: 320,
  TABLET: 767,
  DESKTOP: 1199,
}

export const CLASS = {
  OVERLAY: 'overlay',
  SCROLL_LOGO: 'scroll-logo',
}

export const SELECTOR = {
  'BODY': $('body'),
  'STAGE': $('#stage'),
  'MAIN_SLIDER': $('.main-slider'),
  'MAIN_SEARCH': $('.main-search'),
  'MAIN_SEARCH_SIMPLE': $('.main-search--simple'),
  'MAIN_SEARCH_ADVANCED': $('.main-search--advanced'),
  'FORM_FILTERS_OFFERS': $('.form-filters-offers'),
  'FORM_BOOKING': $('.form-booking'),
  'OVERLAY': $(`.${CLASS.OVERLAY}`),
}

// ----------------------------------------------------------------------------
// Server communication
// ----------------------------------------------------------------------------

/**
 * Private base class for all requests implementing a conversion method
 * that filters unset data
 */
class Request {
  /**
   * Convert fields to an object without empty values
   */
  toRequestData () {
    const isNull = x => x === null
    const isArray = x => x instanceof Array
    const isFunction = x => x instanceof Function
    const isEmptyArray = x => isArray(x) && x.length === 0
    const isEmptyField = x => isNull(x) || isEmptyArray(x)
    const propertyToObject = name => ({ [name]: this[name] })
    const mergeObjects = (x, y) => Object.assign({}, x, y)

    return Object.getOwnPropertyNames(this)
      .filter(property => !(isEmptyField(this[property]) || isFunction(this[property])))
      .reduce((acc, property) => mergeObjects(acc, propertyToObject(property)), {})
  }

  toQueryString () {
    const data = this.toRequestData()
    return Object.keys(data)
      .map(property => {
        if (data[property] instanceof Array) {
          return `${property}=${data[property].join(',')}`
        } else {
          return `${property}=${data[property]}`
        }
      })
      .join('&')
  }

  defineISODateField (name) {
    let store = null
    Object.defineProperty(this, name, {
      get: () => store,
      set: value => { store = this.convertToISOString(value) },
    })
  }

  convertToISOString (value) {
    if (value._isAMomentObject) return value.utc().local().format()
    if (value instanceof Date) return value.toISOString()
    if (value instanceof String) return value
    throw new Error(`Don't know how to convert "${value}" to ISO date string`)
  }

  defineArrayField (name) {
    const store = []
    Object.defineProperty(this, name, {
      get: () => store,
    })
  }
}

/**
 * API Request for getting search constraints, such as airports, destinations and dates
 */
export class SearchConstraintsRequest extends Request {
  Duration = null
  Price = null
  AdultsCount = null
  ChildrenCount = null
  Room = null
  BoardStandards = null
  OfferId = null

  /**
   * Default constructor.
   *
   * To create a request based on a form use "fromWFFMForm" static method instead
   */
  constructor () {
    super()
    this.defineArrayField('Destinations')
    this.defineArrayField('DestinationAirports')
    this.defineISODateField('DepartureDateMin')
    this.defineISODateField('ReturnDateMax')
    this.defineArrayField('ChildrenBirthDates')
  }

  /**
   * Create a request based on form data
   *
   * @param {HTMLFormElement} WFFM form to get the values from
   */
  static fromWFFMForm (form) {
    const request = new SearchConstraintsRequest()

    form.querySelectorAll('.custom-select-country option[checked]')
      .map(option => option.value)
      .forEach(destination => request.Destinations.push(destination))

    form.querySelectorAll('.custom-select-airport option[checked]')
      .map(option => option.value)
      .forEach(destination => request.DestinationAirports.push(destination))

    const selectedDuration = form.querySelector('.custom-select-duration input[type=radio][checked]')
    request.Duration = selectedDuration ? selectedDuration.value : ''

    const dates = form.querySelectorAll('.custom-select-date input[type=text]')
    request.DepartureDateMin = moment(dates[0].value, 'DD.MM.YYYY')
    request.ReturnDateMax = moment(dates[1].value, 'DD.MM.YYYY')

    const people = form.querySelectorAll('.custom-select-people input[type=text]')
    request.AdultsCount = parseInt(people[0].value)
    request.ChildrenCount = parseInt(people[1].value)

    form.querySelectorAll('.custom-select-people input[type=text]').toArray()
      .skip(2) // First 2 controls are for count of adults and count of children
      .take(request.ChildrenCount)
      .map(option => option.value)
      .forEach(birthday => {
        request.ChildrenBirthDates.push(birthday)
      })

    const price = form.querySelector('.custom-select-price-person input[type=radio]:checked')
    if (price) request.Price = price.value
    const room = form.querySelector('.custom-select-room input[type=radio]:checked')
    if (room) request.Room = room.value
    const boardStandard = form.querySelector('.custom-select-food input[type=radio]:checked')
    if (boardStandard) request.BoardStandards = boardStandard.value

    request.OfferId = new QueryString().get('offercode')

    return request
  }
}

export class SearchConstraintsResponse {
  minDate = null
  maxDate = null
  validRanges = null
  destinations = []
  airports = []
  prices = []
  boardStandards = []
  offerTypes = []

  static fromResponseData (data) {
    const response = new SearchConstraintsResponse()
    const minDate = data.MinDate ? data.MinDate : moment()
    const maxDate = data.MaxDate ? data.MaxDate : moment().add(1, 'year').endOf('year')

    response.minDate = SearchConstraintsResponse._parseDate(minDate)
    response.maxDate = SearchConstraintsResponse._parseDate(maxDate)
    response.validRanges = getValidRanges(data.Durations)
    response.destinations = cloneDeep(data.Destinations)
    response.airports = cloneDeep(data.DestinationAirports)
    response.prices = cloneDeep(data.Prices)
    response.boardStandards = cloneDeep(data.Meals)
    response.offerTypes = cloneDeep(data.OfferTypes)
    response.adults = {
      min: data.MinAdultsCount,
      max: data.MaxAdultsCount,
    }

    return response
  }

  static _parseDate (date) {
    if (moment.isMoment(date)) return date
    return moment(parseInt(date.replace(/\/Date\((\d+)\)\//, '$1'))).startOf('day')
  }
}

/**
 * @param {ModalContentRequest} request modal parameters
 */
export class ModalContentRequest extends Request {
  url = null
  ModalParameter = null

  constructor (url) {
    super()

    this.url = url
    // Temporary defined field
    this.defineArrayField('ModalParameter')
  }
}

export class ModalContentResponse {
  title = null
  subtitle = null
  content = null
  headline = null
  type = null

  static fromResponseData (data) {
    const response = new ModalContentResponse()
    response.title = data.Title
    response.subtitle = data.Subtitle
    response.content = data.Content
    response.headline = data.Headline
    response.type = typeof data.Content

    return response
  }
}

export class NewsletterPopupParameterListRequest extends Request {
}

export class NewsletterPopupParameterListResponse {
  list = []

  static fromResponseData (data) {
    const response = new NewsletterPopupParameterListResponse()
    response.list = data

    return response
  }
}

export class InteractiveMapContentRequest extends Request {
}

export class InteractiveMapContentResponse {
  static fromResponseData (data) {
    const response = new InteractiveMapContentResponse()
    response.latitude = data.Settings.Latitude
    response.longitude = data.Settings.Longitude
    response.zoom = data.Settings.Zoom
    response.styles = data.Settings.CustomStyles
    response.regions = data.Regions
    response.airports = data.Airports
    response.destinationAirports = data.DestinationAirports
    response.matrix = data.Matrix

    return response
  }
}

export class HotelMapContentRequest extends Request {
}

export class HotelMapContentResponse {
  static fromResponseData (data) {
    const response = new HotelMapContentResponse()
    response.latitude = data.Latitude
    response.longitude = data.Longitude
    response.zoom = data.Zoom
    response.styles = data.CustomStyles

    return response
  }
}

export class SalespointsMapContentRequest extends Request {
}

export class SalespointsMapContentResponse {
  static fromResponseData (data) {
    const response = new SalespointsMapContentResponse()
    response.latitude = data.Settings.Latitude
    response.longitude = data.Settings.Longitude
    response.zoom = data.Settings.Zoom
    response.styles = data.Settings.CustomStyles
    response.voivodeships = data.Voivodeships
    response.cities = data.Cities
    response.offices = data.Offices

    return response
  }
}

export class ListingMapContentRequest extends Request {
}

export class ListingMapContentResponse {
  static fromResponseData (data) {
    const response = new ListingMapContentResponse()
    response.latitude = data.Settings.Latitude
    response.longitude = data.Settings.Longitude
    response.zoom = data.Settings.Zoom
    response.styles = data.Settings.CustomStyles

    return response
  }
}

/**
 * API request for getting flight schedules
 */
export class FlightScheduleRequest extends Request {
}

export class FlightScheduleResponse {
  flights = []
  weekDay = [ 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela' ]

  static fromResponseData (data) {
    const response = new FlightScheduleResponse()
    response.flights = data.map((flight, index) => ({
      id: index,
      flightDirection: flight.FlightDirection,
      fromAirportCode: flight.FromAirportCode,
      fromAirport: flight.FromAirport,
      toAirportCode: flight.ToAirportCode,
      toAirport: flight.ToAirport,
      flightNumber: flight.FlightNumber,
      takeoffTime: !flight.TakeoffTime ? '' : flight.TakeoffTime,
      landingTime: !flight.LandingTime ? '' : flight.LandingTime,
      flightTime: flight.FlightTime,
      carrierName: flight.CarrierName,
      fromDate: flight.FromDate,
      toDate: flight.ToDate,
      weekDay: FlightScheduleResponse._parseWeekDay(flight.WeekDay),
    }))

    return response
  }

  static _parseWeekDay (weekDay) {
    if (weekDay - 1 === -1) return new Number(6)
    else return new Number(weekDay - 1)
  }
}

export class RodoRegulationsRequest extends Request {
}

export class RodoRegulationsResponse {
  preamble = ''
  body = []

  static fromResponseData (data) {
    const response = new RodoRegulationsResponse()
    response.preamble = data.Header
    response.body = data.Paragraphs

    return response
  }
}

// TODO: the initial load of offers is dictated by some strange url passed on via "data-first-load"
//      attribute of a div with class "load-offers". I'm going to keep it for now as-is preparing
//      for the next iteration with the class below

export class OffersListRequest extends Request {
  Adults = 2
  Children = 0
  DateOfDeparture = moment().format('YYYYMMDD')
  DateOfReturn = moment().format('YYYYMMDD')
  ObjectType = 'H,R,AP'
  pageFrom = 0

  static getInitialUrl () {
    const loadOffersDiv = document.querySelector('.load-offers')
    if (loadOffersDiv && loadOffersDiv.dataset.firstLoad) {
      return loadOffersDiv.dataset.firstLoad
    } else {
      throw new Error('Missing div with class load-offers and data-first-load attribute')
    }
  }

  /**
   * Listing can be configured in some way and offer search (this blue one ;)) will not know
   * about it so if listing is configured with 'setFilters' param that equals 'true' we need
   * to 'copy' configured data into our search fields.
   *
   * @returns {Boolean} whether params should be copied into search or not
   */
  static shouldSetListingFiltersInSearch () {
    return OffersListRequest.getInitialUrl().search('setFilters=true') !== -1
  }
}

export class OffersListResponse {
  offers = []

  static fromResponseData (data) {
    const response = new OffersListResponse()
    /* eslint-disable camelcase */
    response.offers = data.map(offer => {
      offer.id = offer.Merlin_Id
      offer.Hotel_SubBrands = offer.Hotel_SubBrands.map((subBrand, index) => {
        subBrand.id = index
        return subBrand
      })
      offer.Merlin_AdultPrice_V = offer.Merlin_AdultPrice.toNumber()
      offer.Merlin_FullPriceParsed_V = offer.Merlin_FullPriceParsed.toNumber()
      offer.url = `${offer.Hotel_OfferUrlWithOfferCode}${offer.Merlin_Id}${offer.Query_AdultsChildenQueryString}${offer.Query_AdditionalFiltersQueryString}`
      offer.hotelStandard = OffersListResponse.hotelStarsCSSToValue(offer.Hotel_Standard_Stars_Css)
      offer.labels = offer.Hotel_Tags
        .take(4)
        .map((tag, id) => ({ id, name: tag.Name, color: tag.ColorCSS }))
      offer.highlights = [ offer.Hotel_Teaser_Highlights_1, offer.Hotel_Teaser_Highlights_2, offer.Hotel_Teaser_Highlights_3 ]
        .filter(highlight => highlight)
        .map((name, id) => ({ id, name }))
      offer.isNew = offer.Hotel_Is_New
      return offer
    })
    /* eslint-enable camelcase */

    return response
  }

  static hotelStarsCSSToValue (css) {
    let value = css.contains('stars-plus') ? 0.5 : 0
    if (css.contains('two-stars')) value += 2
    else if (css.contains('three-stars')) value += 3
    else if (css.contains('four-stars')) value += 4
    else if (css.contains('five-stars')) value += 5
    else if (css.contains('six-stars')) value += 6

    return value
  }
}

export class OfferAirportsResponse {
  airports = []

  static fromResponseData (data) {
    const result = new OfferAirportsResponse()
    result.airports = data.map((name, id) => ({ id, name }))

    return result
  }
}

export class OfferFoodResponse {
  foods = []

  static fromResponseData (data) {
    const result = new OfferFoodResponse()
    result.foods = data.map((name, id) => ({ id, name }))

    return result
  }
}

export class HotelPicturesResponse {
  images = []

  static fromResponseData (data) {
    const result = new HotelPicturesResponse()
    result.data = data
    result.images = {
      full: data.Pictures.map(p => p.ImageUrl1),
      thumbnail: data.Pictures.map(p => p.ImageUrl2),
      preview: data.Pictures.map(p => p.ImageUrl3),
    }

    return result
  }
}

export class HotelAllOffersRequest extends Request {
  pageFrom = 0
  Price = null
  Adults = null
  Children = null
  Child1 = null
  Child2 = null
  Child3 = null
  Child4 = null
  Child5 = null
  Child6 = null
  Child7 = null
  Child8 = null
  Room = null
  Food = null
  Dates = null
  DurationInterval = null
  SortBy = null
  SortAsc = null

  constructor () {
    super()
    this.defineArrayField('airports')
  }
}

export class HotelAllOffersResponse {
  offers = []
  allOffersCount = null

  static fromResponseData (data) {
    const result = new HotelAllOffersResponse()
    result.offers = data.HotelOffers
    result.offersToLoad = data.OffersToLoad
    result.pageSize = data.PageSize
    const qs = new QueryString()
    const selectedOffer = result.offers.find(offer => qs.get('offercode') == offer.Id)
    if (selectedOffer) {
      selectedOffer.selected = true
    }

    return result
  }
}

export class OffersFilterResponse {
  airports = []
  boardStandards = []
  roomTypes
  priceRanges = []
  durations = []

  static fromResponseData (data) {
    const result = new OffersFilterResponse()
    result.airports = data.Airports.map(airport => ({ value: airport.Id, name: airport.Name, selected: false, enabled: true }))
    result.boardStandards = data.BoardStandards.map(standard => ({ name: standard.Name, value: standard.Id, enabled: true }))
    result.roomTypes = data.RoomTypes.map(roomType => ({ name: roomType.Name, value: roomType.Id, enabled: true }))
    result.priceRanges = data.PriceRanges.map(priceRange => ({ name: priceRange.Name, value: priceRange.Id, enabled: true }))

    // Because backend is not sending us Durations data yet we need to use any duration data we have on page.
    // So as we are on offers list we can use data that exist in form of main search because we need any real data.
    const durations = new DurationWFFMDataExchange(document.querySelector('.main-search form'))
    result.durations = durations.data

    return result
  }
}

export class IdNameListResponse {
  data = []

  static fromResponseData (data) {
    const result = new IdNameListResponse()
    result.data = data

    return result
  }
}

export class TripSurveyDataRequest extends Request {
}

export class TripSurveyDataResponse extends IdNameListResponse {
}

export class SurveyBasicEntryDataValidationRequest extends Request {
  emailAddress = null
  phoneNumber = null
  configurationId = null
}

export class SurveyEntryDataValidationRequest extends Request {
  firstName = null
  lastName = null
  type = null // Reservation|flight|hotel
  reservation = null
  hotel = null
  configurationId = null
  // Departure = null
  // arrival = null

  /**
   * IG-1421
   * @todo: because of actual differences of property names in backend service there was added
   * two properties: "from" and "to" that are equal to "departure" and "arrival" to make sure that
   * rest of code works properly as before.
   */

  from = null
  to = null

  constructor () {
    super()
    this.defineISODateField('date')
  }
}

export class SurveyEntryDataValidationResponse {
  surveyId = null
  surveyPostId = null
  accessKey = null
  payload = null

  static fromResponseData (data) {
    const result = new SurveyEntryDataValidationResponse()

    // One page surveyId: 'e5a166d6-05e1-4724-b523-9fa303646fdd'
    // one page surveyPostId: '37933707-15b0-4277-a544-68f48c9d822c'
    if (data.status === 1) {
      result.surveyId = data.surveyId // 'f9b86538-81b3-40ef-930b-2a31a1aa55a6'
      result.surveyPostId = data.surveyPostId // '1cb98e21-ffe0-4ede-af64-05ed2c626369'
      result.accessKey = data.accessKey
      result.payload = data.payload ? {
        firstName: data.payload.firstName,
        lastName: data.payload.lastName,
        reservation: data.payload.businessCaseNumber,
        hotel: data.payload.destinationHotelName,
        from: data.payload.departureAirportName,
        to: data.payload.destinationRegionName,
        date: data.payload.departureDate,
      } : {}
    } else {
      switch (data.status) {
        case 0:
        case 2:
        case 5:
          throw new Error('Błąd serwera.')
        case 3:
          throw new Error('Niepoprawne dane. Spróbuj ponownie.')
        case 4:
          throw new Error('Dziękujemy za wypełnienie ankiety. Twoje dane są już w naszym systemie.')
        default:
          throw new Error('Błąd serwera.')
      }
    }

    return result
  }
}

export class SurveyCompletedDataRequest extends Request {
  firstName = null
  lastName = null
  type = null // Reservation|flight|hotel
  reservation = null
  hotel = null
  // Departure = null
  // arrival = null

  /**
   * IG-1421
   * @todo: because of actual differences of property names in backend service there was added
   * two properties: "from" and "to" that are equal to "departure" and "arrival" to make sure that
   * rest of code works properly as before.
   */

  from = null
  to = null

  constructor () {
    super()
    this.defineISODateField('date')
  }
}

export class SurveyCompletedDataResponse {
  static fromResponseData (data) {
    const result = new SurveyCompletedDataResponse()
    result.status = data.status

    return result
  }
}

export class AdditionalServicesRequest extends Request {
  offerCode = null
  adults = null
  children = null
  infants = null
  services = null
  child1 = null
  child2 = null
  child3 = null
  child4 = null
  child5 = null
  child6 = null

  static fromSelectedServices (data) {
    const request = new AdditionalServicesRequest()
    request.offerCode = data.offerCode
    request.adults = data.adults
    request.children = data.children
    request.infants = data.infants
    request.services = data.services
    request.child1 = data.child1
    request.child2 = data.child2
    request.child3 = data.child3
    request.child4 = data.child4
    request.child5 = data.child5
    request.child6 = data.child6

    return request
  }
}

export class AdditionalServicesResponse {
  static fromResponseData (data) {
    const result = new AdditionalServicesResponse()
    /**
     * Because whole offer needs to be recalculated whole offer object is returned
     */
    result.offer = data

    return result
  }
}

/* eslint-disable */
export class BookingReservationDataRequest extends Request {
  OfferCode = null
  Adults = null
  Children = null
  InformationFormName = null
  InformationFormSurname = null
  InformationFormEmail = null
  InformationFormNumber = null
  InformationFormStreet = null
  InformationFormHomeNumber = null
  InformationFormCity = null
  InformationFormPostalCode = null
  StatementBooking1 = true
  StatementBooking2 = true
  Payment = 'Przelewy24'
  People = []
  p24_method = null
  AdditionalServices = null;

  constructor () {
    super()
    this.defineArrayField('People')
  }
}
/* eslint-enable */

export class BookingReservationDataResponse {
  paymentUrl = null

  static fromResponseData (data) {
    const result = new BookingReservationDataResponse()
    result.paymentUrl = data.PaymentUrl

    return result
  }
}

/**
 * Private base class for all static data cases that in future
 * will implement a conversion method that filters unset data.
 * For now it should return {Object} from passed component ID.
 */
class StaticData {
  constructor (id) {
    this.globalData = window.grecos && window.grecos.data ? window.grecos.data : {}
    this.componentData = window.grecos && window.grecos.data ? window.grecos.data[id] : {}
  }
}

export class StaticDataExceptions extends StaticData {
  filterExceptions () {
    return Object.keys(this.globalData)
      .filter(item => this.globalData[item])
      .map(item => ({ id: item, exception: this.globalData[item].Exception }))
      .filter(item => item.exception)
  }

  static fromStaticData () {
    return new StaticDataExceptions().filterExceptions()
  }
}

export class BookingHeaderStaticData {
  headline = null
  subHeadline = null
  securityText = null
  logo = {
    alt: null,
    src: null,
    width: null,
    height: null,
  }

  static fromStaticData (id) {
    const { StaticContent } = new StaticData(id).componentData
    const result = new BookingHeaderStaticData()
    result.headline = StaticContent.Main_Header
    result.subHeadline = StaticContent.Sub_Header
    result.securityText = StaticContent.Right_Note
    result.logo = {
      alt: StaticContent.Logo.Alt,
      src: StaticContent.Logo.Src,
      width: StaticContent.Logo.Width,
      height: StaticContent.Logo.Height,
    }

    return result
  }
}

export class BookingSessionBarStaticData {
  text = null
  sessionTimeout = null

  static fromStaticData (id) {
    const { StaticContent } = new StaticData(id).componentData
    const result = new BookingSessionBarStaticData()
    result.text = StaticContent.Text
    result.sessionTimeout = StaticContent.Session_Time
    result.sessionExtendedTime = StaticContent.Session_Extension_Time
    result.sessionWarningTime = StaticContent.Session_Warning_Time
    result.timeoutButtonText = StaticContent.Timeout_Popup_Button_Text
    result.timeoutHeader = StaticContent.Timeout_Popup_Header
    result.timeoutMessage = StaticContent.Timeout_Popup_Message
    result.warningButtonText = StaticContent.Warning_Popup_Button_Text
    result.warningHeader = StaticContent.Warning_Popup_Header
    result.warningMessage = StaticContent.Warning_Popup_Message

    return result
  }
}

export class BookingHotelSummaryStaticData {
  header = null
  hotel = null

  static fromStaticData (id) {
    const { StaticContent, DynamicContent } = new StaticData(id).componentData
    const result = new BookingHotelSummaryStaticData()
    result.header = StaticContent.Header
    result.hotel = DynamicContent

    return result
  }
}

export class BookingClientFormStaticData {
  headline = null
  checkboxLabel = null
  sectionNumber = null

  static fromStaticData (id) {
    const { StaticContent } = new StaticData(id).componentData
    const result = new BookingClientFormStaticData()
    result.headline = StaticContent.Section_Title
    result.sectionNumber = StaticContent.Section_Number
    result.checkboxLabel = StaticContent.Checkbox_Text

    return result
  }
}

export class BookingSidebarStaticData {
  offer = null

  static fromStaticData (id) {
    const { DynamicContent } = new StaticData(id).componentData
    const result = new BookingSidebarStaticData()
    result.offer = DynamicContent

    return result
  }
}

export class BookingAdditionalServicesStaticData {
  additionalServices = null
  sectionNumber = null
  headline = null

  static fromStaticData (id) {
    const { StaticContent, DynamicContent } = new StaticData(id).componentData
    const result = new BookingAdditionalServicesStaticData()
    result.sectionNumber = StaticContent.Section_Number
    result.headline = StaticContent.Section_Title
    result.additionalServices = DynamicContent

    return result
  }
}

export class BookingSummaryStaticData {
  headline = null
  subHeadline = null
  contractTemplate = null
  includedInPrice = null
  buttonText = null
  paymentObligationText = null
  instalmentText = null
  paymentAmountText = null
  paymentDateText = null
  firstSectionText = null
  secondSectionText = null
  thirdSectionText = null
  totalPriceText = null

  static fromStaticData (id) {
    const { StaticContent } = new StaticData(id).componentData
    const result = new BookingHeaderStaticData()
    result.headline = StaticContent.Main_Header_Text
    result.subHeadline = StaticContent.Sub_Header_Text
    result.contractTemplate = StaticContent.Booking_Button_Lower_Text
    result.paymentObligationText = StaticContent.Booking_Button_Upper_Text
    result.buttonText = StaticContent.Booking_Button_Text
    result.includedInPrice = StaticContent.Included_In_Price_Text
    result.instalmentText = StaticContent.Instalment_Text
    result.paymentAmountText = StaticContent.Payment_Amount_Text
    result.paymentDateText = StaticContent.Payment_Date_Text
    result.firstSectionText = StaticContent.Section_1_Header_Text
    result.secondSectionText = StaticContent.Section_2_Header_Text
    result.thirdSectionText = StaticContent.Section_3_Header_Text
    result.totalPriceText = StaticContent.Total_Price_Text

    return result
  }
}

export class BookingPaymentStaticData {
  sectionNumber = null
  headline = null
  paymentMethods = null
  isTraditionalTransferBlocked = null

  static fromStaticData (id) {
    const { StaticContent, DynamicContent } = new StaticData(id).componentData
    const result = new BookingPaymentStaticData()
    result.sectionNumber = StaticContent.Section_Number
    result.headline = StaticContent.Section_Title
    result.paymentMethods = DynamicContent.PaymentMethodOptions
    result.isTraditionalTransferBlocked = DynamicContent.IsTraditionalTransferBlocked
    result.paymentMethodTiles = StaticContent.Types

    return result
  }
}

export class WeatherWidgetDataRequest extends Request {
  offercode = null
  configurationId = null
}

export class WeatherWidgetDataResponse {
  static fromResponseData (data) {
    const result = new WeatherWidgetDataResponse()
    result.DailyData = data.DailyData
    result.YearlyData = data.YearlyData

    return result
  }
}

export class API {
  constructor (root = URL_BASE_API) {
    Object.defineProperty(this, 'root', { value: root })
  }

  /**
   * Gets constraints for a hotel given some filtering parameters
   *
   * @param {String} hotelSlug hotel identifier
   * @param {Object} params search parameters
   * @returns {Promise} Promise that resolves when the request finishes
   */
  getSearchConstraintsForHotelSlug (hotelSlug, request = new SearchConstraintsRequest()) {
    return axios
      .post(`${this.root}/searchfilter/search/?hotelSlug=${hotelSlug}`, request.toRequestData())
      .then(response => SearchConstraintsResponse.fromResponseData(response.data))
  }

  /**
   * Gets constraints for a hotel given some filtering parameters
   *
   * @param {String} hotelCode hotel identifier
   * @param {Object} params search parameters
   * @returns {Promise} Promise that resolves when the request finishes
   */
  getSearchConstraintsForHotelCode (hotelCode, request = new SearchConstraintsRequest()) {
    return axios
      .post(`${this.root}/searchfilter/search/?hotelCode=${hotelCode}`, request.toRequestData())
      .then(response => SearchConstraintsResponse.fromResponseData(response.data))
  }

  /**
   * Gets constraints for a hotel given some filtering parameters
   *
   * @param {String} hotelSlug hotel identifier
   * @param {Object} params search parameters
   * @returns {Promise} Promise that resolves when the request finishes
   */
  getSearchConstraints (request = new SearchConstraintsRequest()) {
    return this.getSearchConstraintsForHotelSlug('', request)
  }

  /**
   * Gets JSON with data to display in modal
   *
   * @param {Object} params modal parameters
   * @returns {Promise<ModalContentResponse>} Promise that resolves when the request finishes
   */
  getJsonForModal (request = new ModalContentRequest()) {
    return axios
      .get(request.url)
      .then(response => ModalContentResponse.fromResponseData(response.data))
  }

  /**
   * Gets list of sitecore subscribers list parameter values
   *
   * @param {Object} params subscribers list parameters
   * @returns {Promise<NewsletterPopupParameterListResponse>}
   */
  getUtmParameterValuesList (request = new NewsletterPopupParameterListRequest()) {
    return axios
      .get(`${this.root}/Newsletter/GetAllParameterValues`, request.toRequestData())
      .then(response => NewsletterPopupParameterListResponse.fromResponseData(response.data))
  }

  /**
   * Get flight schedule data
   */
  getFlightScheduleData (request = new FlightScheduleRequest()) {
    return axios
      .get(`${this.root}/Flights/AllFlights`, request.toRequestData())
      .then(response => FlightScheduleResponse.fromResponseData(response.data))
  }

  getRodoRegulationsContent (request = new RodoRegulationsRequest()) {
    return axios
      .get(`${this.root}/Rodo/Regulations`, request.toRequestData())
      .then(response => RodoRegulationsResponse.fromResponseData(response.data))
  }

  /**
   * Get list of offers
   */
  getOffersList (url = OffersListRequest.getInitialUrl()) {
    return axios
      .get(url)
      .then(response => OffersListResponse.fromResponseData(response.data))
  }

  /**
   * Get list of all available departure airports for a given offer
   * @param {String} objectCode Merlin object id of the offer
   */
  getOfferAirports (objectCode) {
    return axios
      .get(`${this.root}/OffersList/LoadOfferAirports?objectCode=${objectCode}`)
      .then(response => OfferAirportsResponse.fromResponseData(response.data))
  }

  /**
   * Get list of all available food options for a given offer
   * @param {String} objectCode Merlin object id of the offer
   */
  getOfferFoods (objectCode) {
    return axios
      .get(`${this.root}/OffersList/LoadOfferFood?objectCode=${objectCode}`)
      .then(response => OfferFoodResponse.fromResponseData(response.data))
  }

  getHotelPictures (hotelCode) {
    return axios
      .get(`${this.root}/Hotel/GetHotelPhotosIn3SizesAsJson?w=1280&h=720&HotelGuid=${hotelCode}&startIndex=1`)
      .then(response => HotelPicturesResponse.fromResponseData(response.data))
  }

  /**
   * Get a list of offers for a given hotel and within requested search parameters
   * For displaying in offer configurator and hotel page
   *
   * @param {String} objectCode
   * @param {HotelAllOffersRequest} request
   */
  getHotelAllOffers (objectCode, request = new HotelAllOffersRequest()) {
    return axios
      .get(`${this.root}/Hotel/HotelAllOffersAsJson?objectcode=${objectCode}&${request.toQueryString()}`)
      .then(response => HotelAllOffersResponse.fromResponseData(response.data))
  }

  /**
   * Gets json with settings and data to display within google maps on Home Page
   * @returns {Promise<InteractiveMapContentResponse>} Promise that resolves when the request finishes
   */
  getJsonForInteractiveMap (request = new InteractiveMapContentRequest()) {
    return axios
      .get(`${this.root}/Map/GetRegionsAsJSON`, request.toRequestData())
      .then(response => InteractiveMapContentResponse.fromResponseData(response.data))
  }

  /**
   *
   * Gets json with settings and data to display within google maps on Hotel Page
   * @returns {Promise<HotelMapContentResponse>} Promise that resolves when the request finishes
   */
  getJsonForHotelMap (request = new HotelMapContentRequest()) {
    return axios
      .get(`${this.root}/Map/GetHotelMapJson`, request.toRequestData())
      .then(response => HotelMapContentResponse.fromResponseData(response.data))
  }

  /**
   *
   * Gets json with settings and data to display within google maps on Salespoints Static Page
   * @returns {Promise<SalespointsMapContentResponse>} Promise that resolves when the request finishes
   */
  getJsonForSalespointsMap (request = new SalespointsMapContentRequest()) {
    return axios
      .get(`${this.root}/salemap/GetSalespointsJsonData`, request.toRequestData())
      .then(response => SalespointsMapContentResponse.fromResponseData(response.data))
  }

  /**
   *
   * Gets json with settings and data to display within google maps on Listing Page
   * @returns {Promise<ListingMapContentResponse>} Promise that resolves when the request finishes
   */
  getJsonForListingMap (request = new ListingMapContentRequest()) {
    return axios
      .get(`${this.root}/ListingMap/GetListingMapSettings`, request.toRequestData())
      .then(response => ListingMapContentResponse.fromResponseData(response.data))
  }

  getOffersFilter (hotelCode) {
    return axios
      .get(`${this.root}/OffersFilter/OffersFilterAsJson?hotelCode=${hotelCode}`)
      .then(response => OffersFilterResponse.fromResponseData(response.data))
  }

  getSurveyTripHotelData (request = new TripSurveyDataRequest()) {
    return axios
      .get(`${this.root}/TripDictionary/GetHotels`, request.toRequestData())
      .then(response => TripSurveyDataResponse.fromResponseData(response.data))
  }

  getSurveyTripDepartures (request = new TripSurveyDataRequest()) {
    return axios
      .get(`${this.root}/TripDictionary/GetDeparturesAirports`, request.toRequestData())
      .then(response => TripSurveyDataResponse.fromResponseData(response.data))
  }

  getSurveyTripArrivals (request = new TripSurveyDataRequest()) {
    return axios
      .get(`${this.root}/TripDictionary/GetArrivalsRegions`, request.toRequestData())
      .then(response => TripSurveyDataResponse.fromResponseData(response.data))
  }

  validateSurveyUserByBookingDetails (request = new SurveyEntryDataValidationRequest()) {
    return axios
      .post(`${this.root}/Survey/ValidateUserByBookingDetails`, request.toRequestData())
      .then(response => SurveyEntryDataValidationResponse.fromResponseData(response.data))
  }

  markSurveyAsCompletedByBookingDetails (request = new SurveyCompletedDataRequest()) {
    return axios
      .post(`${this.root}/Survey/MarkSurveyAsCompletedByBookingDetails`, request.toRequestData())
      .then(response => SurveyCompletedDataResponse.fromResponseData(response.data))
  }

  validateSurveyUserByBasicDetails (request = new SurveyBasicEntryDataValidationRequest()) {
    return axios
      .post(`${this.root}/Survey/ValidateUserByBasicDetails`, request.toRequestData())
      .then(response => SurveyEntryDataValidationResponse.fromResponseData(response.data))
  }

  markSurveyAsCompletedByBasicDetails (request = new SurveyCompletedDataRequest()) {
    return axios
      .post(`${this.root}/Survey/MarkSurveyAsCompletedByBasicDetails`, request.toRequestData())
      .then(response => SurveyCompletedDataResponse.fromResponseData(response.data))
  }

  sendSurveyToEmail (request) {
    return axios
      .post(`${this.root}/Survey/SendEmailWithSurveyData`, { data: JSON.stringify(request.data) })
      .then(response => SurveyCompletedDataResponse.fromResponseData(response.data))
  }

  getWeatherWidgetData ({ offercode, configurationId }, request = new WeatherWidgetDataRequest()) {
    return axios
      .get(`${this.root}/weather/vueComponentAsync?offercode=${offercode}&configurationId=${configurationId}`, request.toRequestData())
      .then(response => WeatherWidgetDataResponse.fromResponseData(response.data))
  }

  getBookingAdditionalServices (request = new AdditionalServicesRequest()) {
    return axios
      .post(`${this.root}/calculation/calculate`, request.toRequestData())
      .then(response => AdditionalServicesResponse.fromResponseData(response.data))
  }

  getBookingReservationData (request = new BookingReservationDataRequest()) {
    return axios
      .post(`${this.root}/order/create`, request)
      .then(response => BookingReservationDataResponse.fromResponseData(response.data))
  }

  getStaticDataException () {
    return StaticDataExceptions.fromStaticData()
  }

  getBookingHeaderStaticData (id) {
    return BookingHeaderStaticData.fromStaticData(id)
  }

  getBookingSessionBarStaticData (id) {
    return BookingSessionBarStaticData.fromStaticData(id)
  }

  getBookingHotelSummaryStaticData (id) {
    return BookingHotelSummaryStaticData.fromStaticData(id)
  }

  getBookingClientFormStaticData (id) {
    return BookingClientFormStaticData.fromStaticData(id)
  }

  getBookingSidebarStaticData (id) {
    return BookingSidebarStaticData.fromStaticData(id)
  }

  getBookingSummaryStaticData (id) {
    return BookingSummaryStaticData.fromStaticData(id)
  }

  getBookingAdditionalServicesStaticData (id) {
    return BookingAdditionalServicesStaticData.fromStaticData(id)
  }

  getBookingPaymentStaticData (id) {
    return BookingPaymentStaticData.fromStaticData(id)
  }
}



// WEBPACK FOOTER //
// ./src/modules/api.js